import React, { useCallback } from "react";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

const usePanoramaSetup = ({
  mountRef,
  imageUrl,
  useOrientation,
  isZooming,
  compassRef,
}: {
  mountRef: React.MutableRefObject<HTMLDivElement | null>;
  imageUrl: string;
  useOrientation: boolean;
  isZooming: React.MutableRefObject<boolean>;
  compassRef: React.MutableRefObject<HTMLDivElement | null>;
}) => {
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Controls
  const isDragging = useRef(false);
  const previousMousePosition = useRef({
    x: 0,
    y: 0,
  });

  // Camera position and inertia
  const lon = useRef(0);
  const lat = useRef(0);
  const phi = useRef(0);
  const theta = useRef(0);

  const inertiaVelocityX = useRef(0);
  const inertiaVelocityY = useRef(0);
  const inertia = useRef(false);

  useEffect(() => {
    if (!mountRef.current) return;
    const container = mountRef.current;

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Renderer
    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);

    container.appendChild(renderer.domElement);

    // Scene
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.PerspectiveCamera(90, width / height, 0.1, 1000);
    camera.position.set(0, 0, 0);
    cameraRef.current = camera; // Assigning camera to the ref
    scene.add(camera);

    // Texture Loader
    const loader = new THREE.TextureLoader();
    loader.load(
      imageUrl,
      (texture) => {
        const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
        texture.anisotropy = maxAnisotropy;

        // Sphere
        const geometry = new THREE.SphereGeometry(500, 60, 40);
        geometry.scale(-1, 1, 1); // Invert the geometry on the x-axis so that all of the faces point inward

        const material = new THREE.MeshBasicMaterial({ map: texture });
        if (material.map) {
          material.map.colorSpace = THREE.SRGBColorSpace;
          material.map.minFilter = THREE.LinearFilter; // Prevents texture blurring
        }
        const sphere = new THREE.Mesh(geometry, material);
        scene.add(sphere);

        setLoaded(true);
      },
      undefined,
      (error) => {
        console.error(error);
      },
    );

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (useOrientation) return;
      isDragging.current = true;
      setUserHasInteracted(true);
      previousMousePosition.current.x = event.clientX;
      previousMousePosition.current.y = event.clientY;
    };

    const onDocumentMouseMove = (event: MouseEvent) => {
      if (useOrientation || isZooming.current) return;
      if (isDragging.current) {
        const deltaX = event.clientX - previousMousePosition.current.x;
        const deltaY = event.clientY - previousMousePosition.current.y;

        lon.current -= deltaX * 0.1;
        lat.current += deltaY * 0.1;
        lat.current = Math.max(-85, Math.min(85, lat.current)); // Constrain the latitude

        inertiaVelocityX.current = deltaX * 0.02;
        inertiaVelocityY.current = deltaY * 0.02;

        previousMousePosition.current = {
          x: event.clientX,
          y: event.clientY,
        };
      }
    };

    const onDocumentMouseUp = () => {
      if (useOrientation) return;
      isDragging.current = false;
      inertia.current = true;
    };

    const applyInertia = () => {
      if (!inertia || useOrientation) return;

      lon.current -= inertiaVelocityX.current;
      lat.current += inertiaVelocityY.current;

      inertiaVelocityX.current *= 0.95; // Adjust for desired inertia effect
      inertiaVelocityY.current *= 0.95;

      // Stop inertia when movement is negligible
      if (
        Math.abs(inertiaVelocityX.current) < 0.1 &&
        Math.abs(inertiaVelocityY.current) < 0.1
      ) {
        inertia.current = false;
      }
    };

    const animate = () => {
      requestAnimationFrame(animate);

      // Apply inertia
      if (inertia.current && !isDragging.current) {
        applyInertia();
      }

      phi.current = THREE.MathUtils.degToRad(90 - lat.current);
      theta.current = THREE.MathUtils.degToRad(lon.current);

      const x = 500 * Math.sin(phi.current) * Math.cos(theta.current);
      const y = 500 * Math.cos(phi.current);
      const z = 500 * Math.sin(phi.current) * Math.sin(theta.current);

      if (!useOrientation) {
        camera.lookAt(x, y, z);
      }

      // Update compass rotation
      if (compassRef.current) {
        const compassRotation = lon.current;
        compassRef.current.style.transform = `translateX(-50%) rotate(${compassRotation}deg)`;
      }

      // Render the 3D scene
      renderer.render(scene, camera);
    };

    container.addEventListener("pointerdown", onDocumentMouseDown, false);
    container.addEventListener("pointermove", onDocumentMouseMove, false);
    container.addEventListener("pointerup", onDocumentMouseUp, false);

    animate();

    // Cleanup
    return () => {
      container.removeEventListener("pointerdown", onDocumentMouseDown);
      container.removeEventListener("pointermove", onDocumentMouseMove);
      container.removeEventListener("pointerup", onDocumentMouseUp);

      container.removeChild(renderer.domElement);
    };
  }, [imageUrl, useOrientation, isZooming, compassRef, mountRef]);

  const resetView = useCallback(() => {
    const duration = 1000; // Transition duration in milliseconds
    let startTime: number | null = null; // Start time initialization

    function smoothTransition(timestamp: number) {
      if (!startTime) startTime = timestamp; // Set start time on the first frame
      const elapsedTime = timestamp - startTime;
      const fraction = Math.min(elapsedTime / duration, 1); // Calculate transition progress

      // Calculate current state based on ease function (here, we use linear interpolation)
      const ease = (t: number) => t; // Linear easing function, replace with any easing function you prefer
      const currentEase = ease(fraction);

      // Smoothly interpolate lon and lat values
      lon.current = (1 - currentEase) * lon.current; // Smoothly decrease lon to 0
      lat.current = (1 - currentEase) * lat.current; // Smoothly decrease lat to 0

      // Optionally reset inertia if part of your system
      inertiaVelocityX.current = 0;
      inertiaVelocityY.current = 0;
      inertia.current = false;

      if (elapsedTime < duration) {
        requestAnimationFrame(smoothTransition); // Continue animation until duration is met
      }
    }

    requestAnimationFrame(smoothTransition); // Start the animation
  }, [lon, lat, inertiaVelocityX, inertiaVelocityY, inertia]); // Dependence on refs themselves is unnecessary, but keeping for consistency

  return {
    loaded,
    userHasInteracted,
    cameraRef,
    resetView,
    lon,
  };
};

export default usePanoramaSetup;
